import "./LiveTracking.scss";
import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  ContainerStats,
  JobBreakUpDetail,
  JobOrder,
} from "../../interfaces/job.interface";
import {
  containerSizeStringToNumber,
  dateFromNow,
  formatEpochDate,
  getContainerStatsFromJobOrder,
  getContainerStatus,
  mergeDouble20s,
  transformTruckNumber,
} from "../../utils/general";
import { ReactComponent as TruckIcon } from "../../assets/icons/truck.svg";
import { ReactComponent as TruckOnMapIcon } from "../../assets/icons/truck-on-map.svg";
import { ReactComponent as StatusIcon } from "../../assets/icons/job-status-icon.svg";
import JobService from "../../services/JobService";
import JobStatus from "../Jobs/JobStatus";
import { ReactComponent as CicleIcon } from "../../assets/icons/circle.svg";
import { DriverLocation } from "../../interfaces/driver.interface";
import { ReactComponent as JobsIcon } from "../../assets/icons/jobs-icon.svg";

interface LiveTrackingProps {
  job: JobOrder;
  jobBreakupDetailId: number;
}

const LiveTracking: React.FC<LiveTrackingProps> = ({
  job,
  jobBreakupDetailId,
}) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [distance, setDistance] = useState<string>("");
  const [estimatedTime, setEstimatedTime] = useState<string>("");
  const [refreshedDate, setRefreshedDate] = useState<number>();
  const [containerStats, setContainerStats] = useState<ContainerStats>();
  const [selectedJobBreakUpDetails, setSelectedJobBreakUpDetails] =
    useState<JobBreakUpDetail>();
  const [selectedDriverId, setSelectedDriverId] = useState<number | null>();
  const [selectedDriverLocation, setSelectedDriverLocation] =
    useState<DriverLocation>();
  const [locationError, setLocationError] = useState<string>("");
  const [mergedJob, setMergedJob] = useState<JobOrder>(mergeDouble20s(job));
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [selectedZoomLevel, setSelectedZoomLevel] = useState<
    number | string | null
  >(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const truckMarkerRef = useRef<google.maps.Marker | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const previousLocationRef = useRef<{ lat: number; lng: number } | null>(null);
  const pathAnimationRef = useRef<google.maps.Polyline | null>(null);
  const refreshInterval = 30 * 1000; // 30 seconds
  const [showPath, setShowPath] = useState(false);
  const pathRef = useRef<google.maps.Polyline | null>(null);
  const pathTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sourceToDestPathRef = useRef<google.maps.Polyline | null>(null);
  const currentToDestPathRef = useRef<google.maps.Polyline | null>(null);

  const updateDriverDirections = useCallback(
    (selectedDriverLocation: DriverLocation) => {
      if (
        selectedDriverLocation.currentLat &&
        selectedDriverLocation.currentLong &&
        job.destLatitude &&
        job.destLongitude
      ) {
        const truckPosition = {
          lat: parseFloat(selectedDriverLocation.currentLat),
          lng: parseFloat(selectedDriverLocation.currentLong),
        };
        const destinationPosition = {
          lat: parseFloat(job.destLatitude),
          lng: parseFloat(job.destLongitude),
        };
        if (map) {
          const directionsService = new google.maps.DirectionsService();
          directionsService.route(
            {
              origin: truckPosition,
              destination: destinationPosition,
              travelMode: google.maps.TravelMode.DRIVING,
              avoidHighways: false,
              avoidTolls: false,
              drivingOptions: {
                departureTime: new Date(Date.now()),
                trafficModel: google.maps.TrafficModel.BEST_GUESS,
              },
            },
            (result, status) => {
              if (status === google.maps.DirectionsStatus.OK && result) {
                // Animate the path
                const path = result.routes[0].overview_path;
                if (path) {
                  // Only create a new animation if we don't already have one
                  // or if the showPath state is false
                  if (!pathAnimationRef.current || !showPath) {
                    // Clean up previous animation if it exists
                    if (pathAnimationRef.current) {
                      pathAnimationRef.current.setMap(null);
                    }

                    // Create new animation
                    animatePath(path, map);
                  }
                }

                const leg = result.routes[0].legs[0];
                if (leg) {
                  setDistance(leg.distance?.text || "N/A");
                  setEstimatedTime(leg.duration_in_traffic?.text || "N/A");
                }
              } else {
                console.error(`Error fetching route: ${status}`);
              }
            }
          );
        }
      }
    },
    [job.destLatitude, job.destLongitude, map, showPath]
  );

  // Update map when driver location changes
  useEffect(() => {
    if (!mapRef.current || !selectedDriverLocation) return;

    const updateMap = () => {
      if (
        !selectedDriverLocation.currentLat ||
        !selectedDriverLocation.currentLong
      ) {
        setLocationError("Driver location is currently unavailable");
        setDistance("N/A");
        setEstimatedTime("N/A");

        // Hide truck marker when location is unavailable
        if (truckMarkerRef.current) {
          truckMarkerRef.current.setMap(null);
        }

        // Show source to destination path when driver location is unavailable
        showSourceToDestinationPath();
        return;
      }

      setLocationError(""); // Clear any previous error
      const currentPosition = {
        lat: parseFloat(selectedDriverLocation.currentLat.toString()),
        lng: parseFloat(selectedDriverLocation.currentLong.toString()),
      };

      // Update truck marker position
      if (truckMarkerRef.current) {
        // Make sure the marker is visible on the map
        truckMarkerRef.current.setMap(mapRef.current);
        truckMarkerRef.current.setPosition(currentPosition);

        // Update info window content
        if (infoWindowRef.current) {
          const content = `
            <div class="custom-info-window">
              <div class="header">Truck Location</div>
              <div class="content">
                <strong>Truck Number:</strong>
                <span>${
                  selectedJobBreakUpDetails?.truckIdName?.name
                    ? transformTruckNumber(
                        selectedJobBreakUpDetails.truckIdName.name
                      )
                    : "N/A"
                }</span>
                <strong>Container No:</strong>
                <span>${
                  selectedJobBreakUpDetails?.isDoubleTwenty
                    ? selectedJobBreakUpDetails.doubleTwentyDetails
                        ?.map((detail) => detail.containerNumber)
                        .join(", ")
                    : selectedJobBreakUpDetails?.containerNumber || "N/A"
                }</span>
         
              </div>
            </div>
          `;
          infoWindowRef.current.setContent(content);
          infoWindowRef.current.open(mapRef.current, truckMarkerRef.current);
        }
      }

      // Always update path when driver location changes
      updateDriverDirections(selectedDriverLocation);

      // Only update zoom and center if no zoom level is selected
      if (!selectedZoomLevel) {
        // Center map on truck with smooth animation
        const currentZoom = mapRef.current?.getZoom();
        mapRef.current?.panTo(currentPosition);
        if (currentZoom) {
          mapRef.current?.setZoom(currentZoom);
        }
      } else {
        // Just pan to the truck position without changing zoom
        mapRef.current?.panTo(currentPosition);
      }

      // Make sure the path animation is still visible
      if (pathAnimationRef.current) {
        pathAnimationRef.current.setMap(mapRef.current);
      }
    };

    updateMap();
  }, [
    selectedDriverLocation,
    selectedJobBreakUpDetails,
    job.jobStatus,
    distance,
    estimatedTime,
    updateDriverDirections,
    selectedZoomLevel,
  ]);

  // Calculate bearing between two points
  const calculateBearing = (
    start: google.maps.LatLng,
    end: google.maps.LatLng
  ) => {
    const startLat = (start.lat() * Math.PI) / 180;
    const startLng = (start.lng() * Math.PI) / 180;
    const endLat = (end.lat() * Math.PI) / 180;
    const endLng = (end.lng() * Math.PI) / 180;

    const y = Math.sin(endLng - startLng) * Math.cos(endLat);
    const x =
      Math.cos(startLat) * Math.sin(endLat) -
      Math.sin(startLat) * Math.cos(endLat) * Math.cos(endLng - startLng);
    const bearing = (Math.atan2(y, x) * 180) / Math.PI;
    return (bearing + 360) % 360;
  };

  // Animate path drawing
  const animatePath = (path: google.maps.LatLng[], map: google.maps.Map) => {
    if (pathAnimationRef.current) {
      pathAnimationRef.current.setMap(null);
    }

    // Create the main polyline with initial path
    const polyline = new google.maps.Polyline({
      path: path,
      geodesic: true,
      strokeColor: "#1062b9", // Blue color
      strokeOpacity: 0.8,
      strokeWeight: 5,
      map: map,
      zIndex: 1,
    });

    // Store reference for cleanup
    pathAnimationRef.current = polyline;

    // Create shimmering effect
    let dashOffset = 0;
    const dashLength = 20; // Length of each dash
    const gapLength = 10; // Length of gap between dashes
    const totalLength = dashLength + gapLength;

    const animateShimmer = () => {
      dashOffset = (dashOffset + 1) % totalLength;

      // Create the dash pattern
      const dashPattern = `${dashLength} ${gapLength}`;
      const offset = `${dashOffset} ${totalLength - dashOffset}`;

      polyline.setOptions({
        strokeOpacity: 0.8,
        strokeWeight: 5,
        icons: [
          {
            icon: {
              path: "M 0,-1 0,1",
              strokeOpacity: 1,
              strokeWeight: 5,
              scale: 1,
            },
            offset: `${dashOffset}px`,
            repeat: `${totalLength}px`,
          },
        ],
      });

      requestAnimationFrame(animateShimmer);
    };

    // Start the shimmer animation
    animateShimmer();

    // Return a cleanup function
    return () => {
      polyline.setMap(null);
    };
  };

  const setJobBreakupDetail = (jobBreakupDetailId: number) => {
    for (const jobBreakup of mergedJob.jobBreakups) {
      for (const detail of jobBreakup.jobBreakUpDetails) {
        if (detail.isDoubleTwenty) {
          if (
            detail.doubleTwentyDetails?.some(
              (dtObject) => dtObject.jobBreakupDetailId === jobBreakupDetailId
            )
          ) {
            setSelectedJobBreakUpDetails(detail);
          }
        } else {
          if (detail.jobBreakupDetailId === jobBreakupDetailId) {
            setSelectedJobBreakUpDetails(detail);
          }
        }
      }
    }
  };

  const getDriverIdFromJobBreakupDetailId = (
    jobBreakupDetailId: number
  ): number | null => {
    for (const jobBreakup of job.jobBreakups) {
      for (const detail of jobBreakup.jobBreakUpDetails) {
        if (detail.jobBreakupDetailId === jobBreakupDetailId) {
          return detail.driverIdName?.id ?? null;
        }
      }
    }
    return null; // Return null if the ID is not found
  };

  const refreshJobDetails = async (driverId: number) => {
    if (job.jobStatus !== "COMPLETED") {
      setRefreshedDate(Date.now());
      try {
        const driverDetails = await JobService.trackDriver(driverId);
        const currentDriverLocation: DriverLocation = {
          currentLat: driverDetails.data.currentLat,
          currentLong: driverDetails.data.currentLong,
        };

        // Only update if the location has actually changed
        setSelectedDriverLocation((prevLocation) => {
          if (
            !prevLocation || // No previous location
            prevLocation.currentLat !== currentDriverLocation.currentLat ||
            prevLocation.currentLong !== currentDriverLocation.currentLong
          ) {
            console.log("Location updated:", currentDriverLocation);
            return currentDriverLocation;
          }
          return prevLocation;
        });
      } catch (error) {
        console.error("Error refreshing driver details", error);
      }
    }
  };

  // Initialize map and set up driver tracking
  useEffect(() => {
    const initializeMap = async () => {
      if (!window.google) {
        console.error("Google Maps API not loaded");
        return;
      }

      const mapOptions: google.maps.MapOptions = {
        zoom: 12, // Set a default zoom level
        center: {
          lat: parseFloat(job.sourceLatitude),
          lng: parseFloat(job.sourceLongitude),
        },
        streetViewControl: false,
        fullscreenControl: true,
        gestureHandling: "greedy", // Allow zooming with one finger
        zoomControl: false, // Hide default zoom controls
        styles: [
          {
            elementType: "geometry",
            stylers: [
              {
                color: "#f5f5f5",
              },
            ],
          },
          {
            elementType: "labels.icon",
            stylers: [
              {
                visibility: "off",
              },
            ],
          },
          {
            elementType: "labels.text.fill",
            stylers: [
              {
                color: "#616161",
              },
            ],
          },
          {
            elementType: "labels.text.stroke",
            stylers: [
              {
                color: "#f5f5f5",
              },
            ],
          },
          {
            featureType: "administrative.land_parcel",
            elementType: "labels.text.fill",
            stylers: [
              {
                color: "#bdbdbd",
              },
            ],
          },
          {
            featureType: "poi",
            elementType: "geometry",
            stylers: [
              {
                color: "#eeeeee",
              },
            ],
          },
          {
            featureType: "poi",
            elementType: "labels.text.fill",
            stylers: [
              {
                color: "#757575",
              },
            ],
          },
          {
            featureType: "poi.park",
            elementType: "geometry",
            stylers: [
              {
                color: "#e5e5e5",
              },
            ],
          },
          {
            featureType: "poi.park",
            elementType: "labels.text.fill",
            stylers: [
              {
                color: "#9e9e9e",
              },
            ],
          },
          {
            featureType: "road",
            elementType: "geometry",
            stylers: [
              {
                color: "#ffffff",
              },
            ],
          },
          {
            featureType: "road.arterial",
            elementType: "labels.text.fill",
            stylers: [
              {
                color: "#757575",
              },
            ],
          },
          {
            featureType: "road.highway",
            elementType: "geometry",
            stylers: [
              {
                color: "#dadada",
              },
            ],
          },
          {
            featureType: "road.highway",
            elementType: "labels.text.fill",
            stylers: [
              {
                color: "#616161",
              },
            ],
          },
          {
            featureType: "road.local",
            elementType: "labels.text.fill",
            stylers: [
              {
                color: "#9e9e9e",
              },
            ],
          },
          {
            featureType: "transit.line",
            elementType: "geometry",
            stylers: [
              {
                color: "#e5e5e5",
              },
            ],
          },
          {
            featureType: "transit.station",
            elementType: "geometry",
            stylers: [
              {
                color: "#eeeeee",
              },
            ],
          },
          {
            featureType: "water",
            elementType: "geometry",
            stylers: [
              {
                color: "#c9c9c9",
              },
            ],
          },
          {
            featureType: "water",
            elementType: "labels.text.fill",
            stylers: [
              {
                color: "#9e9e9e",
              },
            ],
          },
        ],
      };

      const mapInstance = new window.google.maps.Map(
        document.getElementById("map") as HTMLElement,
        mapOptions
      );

      mapRef.current = mapInstance;
      setMap(mapInstance);
      setIsMapLoaded(true);

      // Create info window
      const infoWindow = new google.maps.InfoWindow();
      infoWindowRef.current = infoWindow;

      // Create source marker with info window
      const sourceMarker = new google.maps.Marker({
        position: {
          lat: parseFloat(job.sourceLatitude),
          lng: parseFloat(job.sourceLongitude),
        },
        map: mapInstance,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: "#cbd2d9",
          fillOpacity: 1,
          strokeWeight: 2,
          strokeColor: "#ffffff",
        },
        zIndex: 1000,
        title: "Source",
      });

      // Add click listener for source marker
      sourceMarker.addListener("click", () => {
        const content = `
          <div class="custom-info-window">
            <div class="header">Source Location</div>
            <div class="content">
              <strong>Address:</strong>
              <span>${job.source}</span>
            </div>
          </div>
        `;
        infoWindow.setContent(content);
        infoWindow.open(mapInstance, sourceMarker);
      });

      // Create destination marker with info window
      const destinationMarker = new google.maps.Marker({
        position: {
          lat: parseFloat(job.destLatitude),
          lng: parseFloat(job.destLongitude),
        },
        map: mapInstance,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: "#1062b9",
          fillOpacity: 1,
          strokeWeight: 2,
          strokeColor: "#ffffff",
        },
        zIndex: 1000,
        title: "Destination",
      });

      // Add click listener for destination marker
      destinationMarker.addListener("click", () => {
        const content = `
          <div class="custom-info-window">
            <div class="header">Destination Location</div>
            <div class="content">
              <strong>Address:</strong>
              <span>${job.destination}</span>
            </div>
          </div>
        `;
        infoWindow.setContent(content);
        infoWindow.open(mapInstance, destinationMarker);
      });

      // Create truck marker with custom icon and info window
      const truckMarker = new google.maps.Marker({
        position: {
          lat: parseFloat(job.sourceLatitude),
          lng: parseFloat(job.sourceLongitude),
        },
        map: null, // Initialize with no map to keep it hidden
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 15,
          fillColor: "#01ba65",
          fillOpacity: 1,
          strokeWeight: 3,
          strokeColor: "#ffffff",
        },
        zIndex: 1001,
        title: "Truck",
      });

      // Add click listener for truck marker
      truckMarker.addListener("click", () => {
        const content = `
          <div class="custom-info-window">
            <div class="header">Truck Location</div>
            <div class="content">
              <strong>Truck Number:</strong>
              <span>${
                selectedJobBreakUpDetails?.truckIdName?.name
                  ? transformTruckNumber(
                      selectedJobBreakUpDetails.truckIdName.name
                    )
                  : "N/A"
              }</span>
              <strong>Container No:</strong>
              <span>${
                selectedJobBreakUpDetails?.isDoubleTwenty
                  ? selectedJobBreakUpDetails.doubleTwentyDetails
                      ?.map((detail) => detail.containerNumber)
                      .join(", ")
                  : selectedJobBreakUpDetails?.containerNumber || "N/A"
              }</span>
              <strong>Status:</strong>
              <span>${job.jobStatus}</span>
            </div>
          </div>
        `;
        infoWindow.setContent(content);
        infoWindow.open(mapInstance, truckMarker);
      });

      // Store references
      truckMarkerRef.current = truckMarker;
    };

    initializeMap();

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (truckMarkerRef.current) {
        truckMarkerRef.current.setMap(null);
      }
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
    };
  }, [job, jobBreakupDetailId]);

  // Set up driver location tracking interval and initial fetch
  useEffect(() => {
    if (selectedDriverId && job.jobStatus !== "COMPLETED") {
      // Clear any existing interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Initial fetch
      refreshJobDetails(selectedDriverId);

      // Set up new interval with a longer refresh time to reduce API calls
      intervalRef.current = setInterval(() => {
        refreshJobDetails(selectedDriverId);
      }, 60000); // Refresh every minute instead of 30 seconds
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [selectedDriverId, job.jobStatus]);

  useEffect(() => {
    if (job) {
      if (jobBreakupDetailId) {
        setJobBreakupDetail(jobBreakupDetailId);
        setSelectedDriverId(
          getDriverIdFromJobBreakupDetailId(jobBreakupDetailId)
        );
      }
      setMergedJob(mergeDouble20s(job));
      setContainerStats(getContainerStatsFromJobOrder(job));
    }
  }, [job]);

  const handleTrackSelection = (containerDetails: JobBreakUpDetail) => {
    // console.log("handleTrackSelection");
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    const newDriverId = containerDetails.driverIdName?.id;
    setJobBreakupDetail(containerDetails.jobBreakupDetailId);
    setSelectedDriverId(newDriverId);
  };

  const isTracking = (jobBreakupDetailId: number) => {
    // console.log(selectedJobBreakUpDetails);
    if (selectedJobBreakUpDetails?.isDoubleTwenty) {
      if (
        selectedJobBreakUpDetails.doubleTwentyDetails?.some(
          (dtObject) => dtObject.jobBreakupDetailId === jobBreakupDetailId
        )
      )
        return true;
      return false;
    } else {
      if (selectedJobBreakUpDetails?.jobBreakupDetailId === jobBreakupDetailId)
        return true;
      return false;
    }
  };

  useEffect(() => {
    return () => {
      if (pathAnimationRef.current) {
        pathAnimationRef.current.setMap(null);
        pathAnimationRef.current = null;
      }
    };
  }, []);

  // Function to show source to destination path
  const showSourceToDestinationPath = () => {
    if (!map || !job) return;

    // Clear any existing paths
    if (sourceToDestPathRef.current) {
      sourceToDestPathRef.current.setMap(null);
    }
    if (currentToDestPathRef.current) {
      currentToDestPathRef.current.setMap(null);
    }
    if (pathAnimationRef.current) {
      pathAnimationRef.current.setMap(null);
    }

    // Clear any existing timeout
    if (pathTimeoutRef.current) {
      clearTimeout(pathTimeoutRef.current);
    }

    // Check if source and destination coordinates are available
    if (
      !job.sourceLatitude ||
      !job.sourceLongitude ||
      !job.destLatitude ||
      !job.destLongitude
    ) {
      console.error("Source or destination coordinates are missing");
      return;
    }

    const sourcePosition = {
      lat: parseFloat(job.sourceLatitude as string),
      lng: parseFloat(job.sourceLongitude as string),
    };
    const destinationPosition = {
      lat: parseFloat(job.destLatitude as string),
      lng: parseFloat(job.destLongitude as string),
    };

    // Create a direct path from source to destination with dark color
    const directPath = [
      new google.maps.LatLng(sourcePosition.lat, sourcePosition.lng),
      new google.maps.LatLng(destinationPosition.lat, destinationPosition.lng),
    ];

    // Create a static dark path from source to destination
    sourceToDestPathRef.current = new google.maps.Polyline({
      path: directPath,
      geodesic: true,
      strokeColor: "#333333", // Dark color
      strokeOpacity: 0.8,
      strokeWeight: 3,
      map: map,
    });

    // Fit bounds to show the source and destination
    const bounds = new google.maps.LatLngBounds();
    bounds.extend(
      new google.maps.LatLng(sourcePosition.lat, sourcePosition.lng)
    );
    bounds.extend(
      new google.maps.LatLng(destinationPosition.lat, destinationPosition.lng)
    );
    map.fitBounds(bounds);

    setShowPath(true);

    // Only set timeout to hide path if we have driver location
    if (
      selectedDriverLocation?.currentLat &&
      selectedDriverLocation?.currentLong
    ) {
      pathTimeoutRef.current = setTimeout(() => {
        // Don't clear the path animation here
        setShowPath(false);

        // Focus back on truck
        const truckPosition = {
          lat: parseFloat(selectedDriverLocation.currentLat as string),
          lng: parseFloat(selectedDriverLocation.currentLong as string),
        };
        map.panTo(truckPosition);
        map.setZoom(12);
      }, 5000); // Show path for 5 seconds
    }
  };

  // Function to locate truck
  const locateTruck = () => {
    if (
      !map ||
      !selectedDriverLocation?.currentLat ||
      !selectedDriverLocation?.currentLong
    )
      return;

    const truckPosition = {
      lat: parseFloat(selectedDriverLocation.currentLat),
      lng: parseFloat(selectedDriverLocation.currentLong),
    };

    // Reset selected zoom level
    setSelectedZoomLevel(null);

    // Pan to truck position and set default zoom
    map.panTo(truckPosition);
    map.setZoom(15);

    // If we have a path animation, make sure it's still visible
    if (pathAnimationRef.current) {
      pathAnimationRef.current.setMap(map);
    }

    // Reset the zoom level button text
    const zoomButton = document.querySelector(
      ".zoom-dropdown-container button span:nth-child(2)"
    );
    if (zoomButton) {
      zoomButton.textContent = "Zoom Level";
    }
  };

  // Function to set zoom level
  const setZoomLevel = (level: number | string) => {
    if (
      !map ||
      !selectedDriverLocation?.currentLat ||
      !selectedDriverLocation?.currentLong
    )
      return;

    const truckPosition = {
      lat: parseFloat(selectedDriverLocation.currentLat),
      lng: parseFloat(selectedDriverLocation.currentLong),
    };

    // Store the selected zoom level
    setSelectedZoomLevel(level);

    // First center on truck
    map.panTo(truckPosition);

    if (level === "fit") {
      // Create bounds that include both current location and destination
      const bounds = new google.maps.LatLngBounds();

      // Add current location
      bounds.extend(
        new google.maps.LatLng(
          parseFloat(selectedDriverLocation.currentLat),
          parseFloat(selectedDriverLocation.currentLong)
        )
      );

      // Add destination
      if (job.destLatitude && job.destLongitude) {
        bounds.extend(
          new google.maps.LatLng(
            parseFloat(job.destLatitude),
            parseFloat(job.destLongitude)
          )
        );
      }

      // Fit the map to these bounds with some padding
      map.fitBounds(bounds, {
        top: 50,
        right: 50,
        bottom: 50,
        left: 50,
      });
    } else {
      // Set zoom level and ensure truck is centered
      map.setZoom(level as number);
      map.panTo(truckPosition);
    }
  };

  // Add map controls
  useEffect(() => {
    if (!map) return;

    // Create control container
    const controlDiv = document.createElement("div");
    controlDiv.className = "map-controls";
    controlDiv.style.position = "absolute";
    controlDiv.style.bottom = "20px";
    controlDiv.style.left = "0";
    controlDiv.style.right = "0";
    controlDiv.style.zIndex = "1";
    controlDiv.style.display = "flex";
    controlDiv.style.flexDirection = "row";
    controlDiv.style.alignItems = "center";
    controlDiv.style.justifyContent = "center";
    controlDiv.style.gap = "12px";
    controlDiv.style.margin = "0 auto 20px";
    controlDiv.style.padding = "0";
    controlDiv.style.backgroundColor = "transparent";
    controlDiv.style.boxShadow = "none";
    controlDiv.style.borderRadius = "0";
    controlDiv.style.flexWrap = "wrap";
    controlDiv.style.maxWidth = "90%";
    controlDiv.style.width = "auto";

    // Create buttons
    const createButton = (text: string, icon: string, onClick: () => void) => {
      const button = document.createElement("button");
      button.className = "map-control-button";
      button.style.padding = "8px 12px";
      button.style.backgroundColor = "white";
      button.style.border = "1px solid #ccc";
      button.style.borderRadius = "4px";
      button.style.cursor = "pointer";
      button.style.boxShadow = "0 2px 6px rgba(0,0,0,0.3)";
      button.style.fontSize = "14px";
      button.style.fontWeight = "500";
      button.style.color = "#333";
      button.style.transition = "all 0.2s ease";
      button.style.display = "flex";
      button.style.alignItems = "center";
      button.style.gap = "8px";
      button.style.minWidth = "120px";
      button.style.justifyContent = "center";

      // Add icon
      const iconSpan = document.createElement("span");
      iconSpan.innerHTML = icon;
      iconSpan.style.display = "flex";
      iconSpan.style.alignItems = "center";
      button.appendChild(iconSpan);

      // Add text
      const textSpan = document.createElement("span");
      textSpan.textContent = text;
      button.appendChild(textSpan);

      button.onmouseover = () => {
        button.style.backgroundColor = "#f1f1f1";
      };

      button.onmouseout = () => {
        button.style.backgroundColor = "white";
      };

      button.onclick = onClick;
      return button;
    };

    // Add buttons to control container with icons
    controlDiv.appendChild(
      createButton(
        "Show Route",
        `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 12H21" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round"/>
          <path d="M12 3V21" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round"/>
          <path d="M3 12L12 3L21 12L12 21L3 12Z" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M12 3L12 21" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round"/>
          <path d="M3 12L21 12" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round"/>
          <circle cx="12" cy="12" r="2" fill="#1062b9"/>
        </svg>`,
        showSourceToDestinationPath
      )
    );
    controlDiv.appendChild(
      createButton(
        "Locate Vehicle",
        `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M12 22C16 18 20 14.4183 20 10C20 5.58172 16.4183 2 12 2C7.58172 2 4 5.58172 4 10C4 14.4183 8 18 12 22Z" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M12 2V22" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round"/>
          <path d="M2 10H22" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round"/>
        </svg>`,
        locateTruck
      )
    );

    // Create zoom level dropdown
    const zoomLevels = [
      {
        level: "fit",
        text: "Fit Route",
        icon: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 3H21V21H3V3Z" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M3 12H21" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round"/>
          <path d="M12 3V21" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round"/>
          <path d="M7 7H17" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round"/>
          <path d="M7 17H17" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round"/>
        </svg>`,
      },
      {
        level: 8,
        text: "Highway View",
        icon: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 12H21" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round"/>
          <path d="M12 3V21" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round"/>
          <path d="M3 12L12 3L21 12L12 21L3 12Z" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M12 3L12 21" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round"/>
          <path d="M3 12L21 12" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round"/>
          <circle cx="12" cy="12" r="2" fill="#1062b9"/>
        </svg>`,
      },
      {
        level: 12,
        text: "City View",
        icon: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 21H21" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round"/>
          <path d="M5 21V7L12 3L19 7V21" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M9 21V13H15V21" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>`,
      },
      {
        level: 15,
        text: "Street View",
        icon: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 21H21" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round"/>
          <path d="M3 7H21" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round"/>
          <path d="M3 14H21" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round"/>
          <path d="M3 3L21 3" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round"/>
        </svg>`,
      },
      {
        level: 18,
        text: "GPS View",
        icon: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M12 22C16 18 20 14.4183 20 10C20 5.58172 16.4183 2 12 2C7.58172 2 4 5.58172 4 10C4 14.4183 8 18 12 22Z" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>`,
      },
    ];

    // Create dropdown container
    const dropdownContainer = document.createElement("div");
    dropdownContainer.className = "zoom-dropdown-container";
    dropdownContainer.style.position = "relative";
    dropdownContainer.style.display = "inline-block";

    // Create dropdown button
    const dropdownButton = document.createElement("button");
    dropdownButton.className = "map-control-button";
    dropdownButton.style.padding = "8px 12px";
    dropdownButton.style.backgroundColor = "white";
    dropdownButton.style.border = "1px solid #ccc";
    dropdownButton.style.borderRadius = "4px";
    dropdownButton.style.cursor = "pointer";
    dropdownButton.style.boxShadow = "0 2px 6px rgba(0,0,0,0.3)";
    dropdownButton.style.fontSize = "14px";
    dropdownButton.style.fontWeight = "500";
    dropdownButton.style.color = "#333";
    dropdownButton.style.transition = "all 0.2s ease";
    dropdownButton.style.display = "flex";
    dropdownButton.style.alignItems = "center";
    dropdownButton.style.gap = "8px";
    dropdownButton.style.width = "100%";
    dropdownButton.style.justifyContent = "space-between";

    // Add zoom icon
    const zoomIcon = document.createElement("span");
    zoomIcon.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 4V20" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round"/>
      <path d="M4 12H20" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round"/>
      <path d="M12 4L8 8" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M12 4L16 8" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M12 20L8 16" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M12 20L16 16" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      <circle cx="12" cy="12" r="2" fill="#1062b9"/>
    </svg>`;
    zoomIcon.style.display = "flex";
    zoomIcon.style.alignItems = "center";

    // Add text
    const buttonText = document.createElement("span");
    buttonText.textContent = "Zoom Level";

    // Add dropdown arrow
    const dropdownArrow = document.createElement("span");
    dropdownArrow.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6 9L12 15L18 9" stroke="#1062b9" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;

    // Assemble button
    dropdownButton.appendChild(zoomIcon);
    dropdownButton.appendChild(buttonText);
    dropdownButton.appendChild(dropdownArrow);

    // Create dropdown content
    const dropdownContent = document.createElement("div");
    dropdownContent.className = "zoom-dropdown-content";
    dropdownContent.style.display = "none";
    dropdownContent.style.position = "absolute";
    dropdownContent.style.bottom = "100%";
    dropdownContent.style.left = "0";
    dropdownContent.style.backgroundColor = "white";
    dropdownContent.style.minWidth = "160px";
    dropdownContent.style.boxShadow = "0 2px 10px rgba(0,0,0,0.2)";
    dropdownContent.style.borderRadius = "4px";
    dropdownContent.style.zIndex = "1000";
    dropdownContent.style.marginBottom = "5px";

    // Add zoom level options
    zoomLevels.forEach(({ level, text, icon }) => {
      const option = document.createElement("div");
      option.className = "zoom-option";
      option.style.padding = "8px 12px";
      option.style.cursor = "pointer";
      option.style.display = "flex";
      option.style.alignItems = "center";
      option.style.gap = "8px";
      option.style.transition = "background-color 0.2s";

      option.innerHTML = `
        <span style="display: flex; align-items: center;">${icon}</span>
        <span>${text}</span>
      `;

      option.onmouseover = () => {
        option.style.backgroundColor = "#f1f1f1";
      };

      option.onmouseout = () => {
        option.style.backgroundColor = "white";
      };

      option.onclick = (e) => {
        e.stopPropagation(); // Prevent event bubbling
        setZoomLevel(level);
        dropdownContent.style.display = "none";
        buttonText.textContent = text;
      };

      dropdownContent.appendChild(option);
    });

    // Toggle dropdown
    dropdownButton.onclick = (e) => {
      e.stopPropagation(); // Prevent event bubbling
      const isVisible = dropdownContent.style.display === "block";
      dropdownContent.style.display = isVisible ? "none" : "block";
    };

    // Close dropdown when clicking outside
    document.addEventListener("click", (event) => {
      if (!dropdownContainer.contains(event.target as Node)) {
        dropdownContent.style.display = "none";
      }
    });

    // Assemble dropdown
    dropdownContainer.appendChild(dropdownButton);
    dropdownContainer.appendChild(dropdownContent);
    controlDiv.appendChild(dropdownContainer);

    // Add control container to map
    map.controls[google.maps.ControlPosition.BOTTOM_CENTER].push(controlDiv);

    // Cleanup
    return () => {
      if (sourceToDestPathRef.current) {
        sourceToDestPathRef.current.setMap(null);
      }
      if (currentToDestPathRef.current) {
        currentToDestPathRef.current.setMap(null);
      }
      if (pathTimeoutRef.current) {
        clearTimeout(pathTimeoutRef.current);
      }
      map.controls[google.maps.ControlPosition.BOTTOM_CENTER].clear();
    };
  }, [map, job, selectedDriverLocation]);

  return (
    <div className="live-tracking-component">
      <div id="map" className="map_style" style={containerStyle}></div>
      <div className="live-stats bg-card">
        {/* Job Header and Job Details */}
        <div className="flex justify-between items-center title">
          <div className="left job_id flex items-center gap-[10px]">
            {/* <JobsIcon /> */}
            {job.jobCode}
          </div>
          <div className={`job_status ${job.jobStatus}`}>
            {/* update this once the job status issue is fixed */}
            {/* <JobStatus jobStatus={job.jobStatus} /> */}
            {job.jobStatus === "COMPLETED" ? (
              <JobStatus jobStatus={job.jobStatus} />
            ) : (
              <JobStatus jobStatus={"IN_PROGRESS"} />
            )}
          </div>
        </div>
        {/* {locationError && (
          <div className="location-error">
            <span>{locationError}</span>
          </div>
        )} */}
        <div className="src_dest_cover">
          <div className="src-dest">
            <div className="src">
              <CicleIcon className="source_icon" />
              <div
                className="source_name truncate-two-lines"
                title={job.source}
              >
                {job.source}
              </div>
            </div>
            <div className="route"></div>
            <div className="dest">
              <CicleIcon className="destination_icon" />
              <div
                className="destination_name truncate-two-lines"
                title={job.destination}
              >
                {job.destination}
              </div>
            </div>
          </div>
        </div>

        {/* Job Status Component */}
        <div className="job_details">
          <div className="text-left text-grayText status_title">Job Status</div>
          <div className="flex justify-between items-center">
            <span className="total_dropped_containers">
              {containerStats &&
                Math.round(containerStats.totalJobStatusPercentage)}
              %
            </span>
            <span className="percentage">
              <span className="total_dropped_containers">
                {containerStats?.totalDroppedContainers}{" "}
              </span>

              <span className="total_containers">
                / {containerStats?.totalContainer} dropped
              </span>
            </span>
          </div>
          {/* Progress Bar */}
          <div className="w-full h-3 job_progress_cover">
            <div
              className="h-3 job_progress"
              style={{ width: `${containerStats?.totalJobStatusPercentage}%` }}
            ></div>
          </div>
          {/* {refreshedDate && <div>{dateFromNow(refreshedDate)}</div>} */}
        </div>

        {/* Container List */}
        <div className="truck_list">
          {mergedJob.jobBreakups.flatMap((breakup, index) => {
            const previousCount = job.jobBreakups
              .slice(0, index) // Only consider breakups before the current one
              .reduce(
                (acc, prevBreakup) =>
                  acc +
                  prevBreakup.jobBreakUpDetails.filter(
                    (detail) => detail.containerNumber
                  ).length,
                0
              );

            return breakup.jobBreakUpDetails
              .filter(
                (containerDetail) =>
                  containerDetail.containerNumber &&
                  containerDetail.truckIdName?.name &&
                  getContainerStatus(containerDetail) === "Picked"
              )
              .map((containerDetail, detailIndex) => (
                <div
                  key={previousCount + detailIndex + 1}
                  className={`truck_card mb-4 ${
                    isTracking(containerDetail.jobBreakupDetailId)
                      ? "selected_truck"
                      : ""
                  }`}
                >
                  <div className="flex justify-between items-center header">
                    <div className="flex items-center">
                      <TruckIcon className="text-[#a3a8b4]" />
                      <span className="ml-[6px] truck_no">
                        {containerDetail.truckIdName
                          ? transformTruckNumber(
                              containerDetail.truckIdName.name
                            )
                          : "N/A"}
                      </span>
                    </div>

                    {isTracking(containerDetail.jobBreakupDetailId) ? (
                      <div
                        className={`tracking-style ${
                          !selectedDriverLocation?.currentLat ||
                          !selectedDriverLocation?.currentLong
                            ? "location-unavailable"
                            : ""
                        }`}
                      >
                        <StatusIcon></StatusIcon>
                        <span>
                          {!selectedDriverLocation?.currentLat ||
                          !selectedDriverLocation?.currentLong
                            ? "Location Unavailable"
                            : "Tracking"}
                        </span>
                      </div>
                    ) : (
                      <div
                        className="link-style"
                        onClick={() => handleTrackSelection(containerDetail)}
                      >
                        Track
                      </div>
                    )}
                  </div>

                  {/* Grid Layout for Container Details */}
                  <div className="container_details grid grid-cols-2 gap-2  text-transporterText">
                    <div className="label">
                      <span>Trip ID</span>
                    </div>
                    <div>
                      <span className="value">
                        {containerDetail.jobBreakupDetailId}
                      </span>
                    </div>

                    <div className="label">
                      <span>Container No</span>
                    </div>
                    <div>
                      <span className="value">
                        {containerDetail.isDoubleTwenty
                          ? containerDetail.doubleTwentyDetails
                              ?.map((detail) => detail.containerNumber)
                              .join(", ")
                          : containerDetail.containerNumber}
                      </span>
                    </div>

                    <div className="label">
                      <span>Size & Type</span>
                    </div>
                    <div>
                      <span className="value">
                        {containerDetail.isDoubleTwenty
                          ? containerDetail.doubleTwentyDetails
                              ?.map(
                                (detail) =>
                                  `${containerSizeStringToNumber(
                                    detail.containerSize
                                  )} ${detail.containerType}`
                              )
                              .join(", ")
                          : containerSizeStringToNumber(breakup.containerSize) +
                            " " +
                            breakup.containerType}
                      </span>
                    </div>

                    <div className="label">
                      <span>Driver</span>
                    </div>
                    <div className="value">
                      <span>{containerDetail.driverIdName?.name}</span>
                    </div>

                    <div className="label">
                      <span>Picked at</span>
                    </div>
                    <div className="value">
                      <span>{formatEpochDate(containerDetail.startDate)}</span>
                    </div>

                    {isTracking(containerDetail.jobBreakupDetailId) && (
                      <>
                        <div className="label">
                          <span>Distance to drop</span>
                        </div>
                        <div className="value">
                          <span>{distance}</span>
                        </div>
                        <div className="label">
                          <span>ETA</span>
                        </div>
                        <div className="value">
                          <span>{estimatedTime}</span>
                        </div>

                        {refreshedDate && (
                          <>
                            <div className="label">
                              <span>Last synced</span>
                            </div>
                            <div className="value">
                              <span>{dateFromNow(refreshedDate)}</span>
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ));
          })}
        </div>
      </div>
    </div>
  );
};

export default LiveTracking;

const containerStyle = {
  width: "100%",
  height: "100%",
};
